import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { pageTitleFor, trackPageView } from "services/api-client/matomoTracker";

/**
 * Reports a Matomo page view whenever the route changes.
 *
 * Mounted once, in `DefaultLayout`, so every route goes through the same code
 * path: a page view is what starts a heatmap page view and re-evaluates
 * whether this visitor's session should be recorded.
 *
 * Keyed on the path *and* the query, because a layout flag lives in the query
 * and changing one is a different page: `/?embed=1` and `/` render different
 * DOMs, so they get their own page view and their own recording. Deciding
 * which query changes matter is `trackPageView`'s job — it compares the path
 * and the layout flags and drops everything else, so a filter or a prompt
 * parameter moving still reports nothing.
 *
 * `noRerender` is how `ChatWindow` marks the one navigation that is not the
 * visitor going anywhere: the `replace` that puts the new session id in the
 * URL after the first message. It is read here rather than inferred from the
 * path, which cannot tell that apart from opening an existing conversation
 * from the sidebar.
 */
export const useMatomoPageView = (): void => {
  const { pathname, search, state } = useLocation();
  const sameConversation = Boolean(
    (state as { noRerender?: boolean } | null)?.noRerender,
  );

  useEffect(() => {
    trackPageView({
      title: pageTitleFor(pathname),
      // The one caller that is a real route change, so the one that ends the
      // current heatmap page view and starts another.
      isNavigation: true,
      sameConversation,
    });
    // `sameConversation` belongs to this navigation: re-running when the flag
    // is scrubbed from the history entry would report the page view again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, search]);
};
